import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { IconButton, Paper, SvgIcon, TableSortLabel, Typography, useTheme } from '@mui/material';
import { Trash01 } from '../../../../assets';
import { RetailerHeaderModel } from '../../../../app/services/api/ofdClient';
import { useNavigate } from 'react-router-dom';
import { OnboardingStatusEnum } from '../../Onboarding/enums/OnboardingStatusEnum';
import { UserContext } from '../../../../components/shared/useUser';
import CustomPagination from '../../../../components/forms/Pagination';

interface RolesTableProps {
  retailers: RetailerHeaderModel[];
  deleteRetailer: Function;
  page: number;
  setPage: (page: number) => void;
}

const ManageRetailersTable = ({ retailers, deleteRetailer, page, setPage }: RolesTableProps) => {
  const { t } = useTranslation('manageRetailers');
  const [data, setData] = React.useState(retailers);
  const [order, setOrder] = React.useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof RetailerHeaderModel>('name' as keyof RetailerHeaderModel);
  const navigate = useNavigate();
  const theme = useTheme();
  const { hasPermissionTo } = React.useContext(UserContext);

  const rowsPerPage = 10;
  const maxPages = retailers
    ? Math.max(1, Math.ceil(retailers.length / rowsPerPage))
    : 1;

  React.useEffect(() => {
    setData(retailers);
  }, [retailers]);


  const sortedData = () => {
    const isAsc = order === 'asc';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const comparator = (a: any, b: any) => {

      if (typeof a[orderBy] === 'string') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const fistString = a[orderBy].replace(/<\/?[^>]+(>|$)/g, '');
        const secondString = b[orderBy].replace(/<\/?[^>]+(>|$)/g, '');
        return (isAsc ? fistString.localeCompare(secondString) : secondString.localeCompare(fistString));
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (isAsc ? a[orderBy] - b[orderBy] : b[orderBy] - a[orderBy]) as any;
      }
    };
    return retailers.sort(comparator);
  };

  const handleSearchChange = () => {
    setData(sortedData());
  };

  const handleSort = (property: keyof RetailerHeaderModel) => {
    const isAsc = orderBy === property && order === 'asc';
    if (data) {
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
      handleSearchChange();
    }
  };

  const editNotification = (id: number) => {
    navigate(`/retailers/${id}`);
  };

  const visibleRows = () => {
    return data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  return (
    <TableContainer sx={{ borderRadius: '12px' }}
      component={Paper}>
      <Table
        size="small"
        aria-label="a table"
      >
        <TableHead sx={{ paddingTop: '10px', backgroundColor: theme.palette.custom.gray[200] }}>
          <TableRow>
            <TableCell>
              ID
            </TableCell>
            <TableCell
              width={'30%'}
              align="left">
              <TableSortLabel
                active={orderBy === 'name'}
                direction={order}
                onClick={() => handleSort('name')}>
                {t('name')}
              </TableSortLabel>
            </TableCell>
            <TableCell
              width={'25%'}
              align="left">
              <TableSortLabel
                active={orderBy === 'city'}
                direction={order}
                onClick={() => handleSort('city')}>
                {t('city')}
              </TableSortLabel>
            </TableCell>
            <TableCell
              width={'25%'}
              align="left">
              <TableSortLabel
                active={orderBy === 'country'}
                direction={order}
                onClick={() => handleSort('country')}>
                {t('country')}
              </TableSortLabel>
            </TableCell>
            <TableCell
              align="left">
              <TableSortLabel
                active={orderBy === 'statusId'}
                direction={order}
                onClick={() => handleSort('statusId')}>
                {t('status')}
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleRows().map((row, index) => (
            <TableRow
              key={page * rowsPerPage + index}
              sx={hasPermissionTo(['RetailersDetailsView']) ? { height: '60px', cursor: 'pointer' } : { height: '60px' }}
              onClick={hasPermissionTo(['RetailersDetailsView']) ? () => editNotification(row.retailerId ?? 0) : undefined}
            >
              <TableCell>
                <Typography variant='textXS'
                  fontWeight={600}
                  color={theme.palette.primary[500]}
                  sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                  {page * rowsPerPage + index + 1}
                </Typography>
              </TableCell>
              <TableCell>
                {row.name}
              </TableCell>
              <TableCell>
                {row.city != null && row.city.length > 0 ? row.city : '-'}
              </TableCell>
              <TableCell>
                {row.country != null && row.country.length > 0 ? row.country : '-'}
              </TableCell>
              <TableCell>
                {row.statusId == OnboardingStatusEnum.DRAFT ? t('draft') : t('active')}
              </TableCell>
              <TableCell sx={{ textAlign: 'right' }}>
                {row.statusId == OnboardingStatusEnum.DRAFT &&
                <IconButton
                  disabled={!hasPermissionTo(['RetailersDelete'])}
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteRetailer(row);
                  }}>
                  <SvgIcon fontSize='small'><Trash01 color={theme.palette.error[500]} /></SvgIcon>
                </IconButton>
                }
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <CustomPagination
        page={page}
        setPage={setPage}
        maxPages={maxPages}
        breakpointForChangeDisplay={120000}
      />
    </TableContainer>
  );
};

export default ManageRetailersTable;
